import { useEffect, useState } from 'react';
import type { NoteAppointment } from '@ehr/shared';
import { api } from '../lib/api.js';
import { normalizeTime, type ApptPrefill } from '../lib/apptLink.js';
import { storedTime } from '../lib/timePrefs.js';
import { Modal } from './Modal.js';

/**
 * ehr#437 (Pranav 10-16): "+ Add new" starts here. The client's checked-out appointments, newest
 * first; picking one goes on to "What kind of note?" and the note opens filled in from it (date,
 * start, end, length, the appointment's type, In person or Telehealth). An appointment that already
 * has a draft opens that draft instead (the server refuses a second note anyway); a signed one opens
 * the note. "Start without an appointment" keeps the old way, for a crisis call, a brief contact or
 * case management with nothing on the calendar. ehr#479: the client's group sessions list too
 * (tagged Group); picking one opens the group note page on this client.
 */
export function NoteApptPicker({
  contactId,
  contactName,
  currentUserId,
  onPick,
  onOpenNote,
  onClose,
}: {
  contactId: string;
  contactName: string;
  currentUserId: string | null;
  /** An appointment (null: start without one). */
  onPick: (appt: NoteAppointment | null) => void;
  onOpenNote: (noteId: string) => void;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<NoteAppointment[] | null>(null);
  const [err, setErr] = useState('');
  const [exists, setExists] = useState<NoteAppointment | null>(null);
  useEffect(() => {
    let live = true;
    api
      .get<NoteAppointment[]>(`/contacts/${contactId}/note-appointments`)
      .then((v) => live && setRows(Array.isArray(v) ? v : []))
      .catch(() => {
        if (!live) return;
        setRows([]);
        setErr('Could not load the appointments. You can still start without one.');
      });
    return () => {
      live = false;
    };
  }, [contactId]);
  const first = contactName.split(' ')[0] || 'this client';
  const fmtDate = (d: string) =>
    new Date(`${d}T12:00:00`).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  const draftOf = (a: NoteAppointment) =>
    a.note && (a.note.status === 'draft' || a.note.status === 'changes') ? a.note : null;
  const choose = (a: NoteAppointment) => {
    if (!a.note) return onPick(a);
    if (draftOf(a)) return setExists(a);
    onOpenNote(a.note.id);
  };
  return (
    <Modal
      title="Which appointment is this note for?"
      onClose={onClose}
      className="cn-appt-modal"
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn" onClick={() => onPick(null)}>
            Start without an appointment
          </button>
        </>
      }
    >
      <p className="cn-appt-intro">
        {first}&rsquo;s checked-out appointments. The note fills in from the one you pick; a group
        session opens its group note.
      </p>
      {rows === null ? (
        <p className="cn-appt-empty">Loading appointments&hellip;</p>
      ) : rows.length === 0 ? (
        <p className="cn-appt-empty">
          {err ||
            `${first} has no checked-out appointments. An appointment shows here once it's checked out in the Calendar.`}
        </p>
      ) : (
        <ul className="cn-appt-list">
          {rows.map((a) => {
            const draft = draftOf(a);
            const state = !a.note ? null : draft ? 'Draft note' : 'Note written';
            return (
              <li key={a.appointmentKey}>
                <button
                  type="button"
                  className={`cn-appt-row${a.note ? ' has-note' : ''}`}
                  onClick={() => choose(a)}
                >
                  <span className="cn-appt-when">
                    <b>{fmtDate(a.date)}</b>
                    <span>{storedTime(normalizeTime(a.time)) || a.time}</span>
                  </span>
                  <span className="cn-appt-what">
                    <b>
                      {a.typeLabel}
                      {/* ehr#479: a group session; its note is written in the group note */}
                      {a.group && <span className="cn-badge group">Group</span>}
                    </b>
                    <span>
                      {[
                        a.durationMin ? `${a.durationMin} min` : null,
                        a.telehealth ? 'Telehealth' : 'In person',
                        a.providerName,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  {state ? (
                    <span className={`cn-appt-tag${draft ? ' draft' : ''}`}>{state}</span>
                  ) : (
                    <span className="cn-appt-go">Select</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {exists && (
        <Modal
          title="Progress Note Already Exists"
          onClose={() => setExists(null)}
          actions={
            <>
              <button type="button" className="btn" onClick={() => setExists(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={() => {
                  const id = exists.note!.id;
                  setExists(null);
                  onOpenNote(id);
                }}
              >
                Open the draft
              </button>
            </>
          }
        >
          <p className="pn-modal-p">
            A progress note has already been created for this appointment ({fmtDate(exists.date)})
            and is in Draft.
            {exists.note!.authorId === currentUserId
              ? ' Open it to keep writing instead of starting a second note.'
              : ' It belongs to another clinician; open it to see it.'}
          </p>
        </Modal>
      )}
    </Modal>
  );
}

/** The note's starting point from a picked appointment (the calendar link's prefill, plus place). */
export function prefillFrom(a: NoteAppointment, noteType: string): ApptPrefill {
  return {
    appointmentKey: a.appointmentKey,
    date: a.date,
    time: normalizeTime(a.time),
    // the picked kind; the editor still takes the practice's type matching the appointment's
    sessionType: noteType,
    typeLabel: a.typeLabel,
    durationMin: a.durationMin,
    place: a.telehealth ? 'Telehealth' : 'In person',
  };
}
